/* eslint-disable jsx-a11y/anchor-is-valid */

import { useCheckPrivacySetting } from '@va/dashboard/util-hooks';
import { LayersIcon, LoginIcon, LogoutIcon } from '@va/icons';
import { useLocale, useTranslate } from '@va/localization';
import { PRIVACY_FEATURES } from '@va/standalone/shared/types';
import { PageType } from '@va/types/recordings';
import {
  Button,
  fontWeights,
  PageItemType,
  Paragraph,
  paragraphSizes,
  VisitedPagesTooltip,
} from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { addNumberSeparator, formatWebsitePage } from '@va/util/helpers';
import { useIsFocused, useIsHovering } from '@va/util/hooks';
import classNames from 'classnames';
import React, { useMemo, useRef } from 'react';
import { useHistory } from 'react-router';
import { getMappedPages } from '../context';

type PagesCellProps = {
  pages: PageType[];
  totalCount: number;
  entryPage: string;
  exitPage: string;
  countryCode: string;
  sessionId: string;
  size?: 'small' | 'medium';
  isClickable?: boolean;
};

export const PagesCell: React.FC<PagesCellProps> = ({
  sessionId,
  pages,
  totalCount,
  entryPage,
  exitPage,
  countryCode,
  size = 'medium',
  isClickable = true,
}) => {
  const history = useHistory();
  const { locale } = useLocale();
  const translate = useTranslate();
  const visitedPagesBtnRef = useRef<HTMLButtonElement>(null);
  const entryPageRef = useRef<HTMLAnchorElement>(null);
  const exitPageRef = useRef<HTMLAnchorElement>(null);

  const isHovering = useIsHovering(visitedPagesBtnRef);
  const isButtonFocused = useIsFocused(visitedPagesBtnRef);

  const isEntryPageFocused = useIsFocused(entryPageRef);
  const isExitPageFocused = useIsFocused(exitPageRef);

  const { isTrackingEnabled } = useCheckPrivacySetting();

  const isPageHistoryTrackingEnabled = isTrackingEnabled(PRIVACY_FEATURES.individualPageHistory, countryCode);

  const mappedPages: PageItemType[] = useMemo(() => getMappedPages(pages), [pages]);

  const entryPageWithoutProtocol = formatWebsitePage(entryPage);
  const exitPageWithoutProtocol = formatWebsitePage(exitPage);

  return (
    <div className='w-full flex gap-3 items-center justify-start'>
      <Button
        text={addNumberSeparator(totalCount, locale)}
        icon={(className, color) => <LayersIcon className={className} color={color} />}
        className={classNames('min-w-fit', {
          'text-primary [&_svg>path]:fill-primary': !isHovering,
          'py-9px': size === 'small',
          'cursor-default': !isClickable || !isPageHistoryTrackingEnabled,
        })}
        textClasses={classNames({
          'text-xs': size === 'small',
        })}
        color={isHovering ? 'primary' : 'quinary'}
        ref={visitedPagesBtnRef}
        tooltip={
          <VisitedPagesTooltip
            isPageHistoryTrackingEnabled={isPageHistoryTrackingEnabled}
            title={translate('card.latestVisitors.visitorsHistory.title')}
            items={mappedPages}
          />
        }
        tooltipProps={{
          useDefaultStyle: false,
          interactive: true,
          open: isButtonFocused || undefined,
        }}
        onClick={(event) => {
          if (!isClickable || !isPageHistoryTrackingEnabled) return;
          event.stopPropagation();
          history.push(`/visitors/history/${sessionId}`);
        }}
      />

      <div className='flex flex-col gap-3 overflow-hidden'>
        <a
          target={'_blank'}
          rel='noreferrer'
          ref={entryPageRef}
          aria-disabled={!isPageHistoryTrackingEnabled}
          href={isPageHistoryTrackingEnabled ? entryPage : undefined}
          onClick={(event) => event.stopPropagation()}
        >
          <div className='flex items-center gap-2'>
            <TooltipWrapper content={translate('recordings.entryPage')}>
              <div>
                <LoginIcon color='var(--color-text-secondary)' className='shrink-0' />
              </div>
            </TooltipWrapper>
            <TooltipWrapper
              content={
                isPageHistoryTrackingEnabled ? (
                  <p className='break-words'>{entryPage}</p>
                ) : (
                  translate('panels.recordings.consentless.hiddenPages')
                )
              }
              open={isEntryPageFocused || undefined}
            >
              <Paragraph
                size={paragraphSizes.tiny}
                colorClassName='text-text-secondary'
                className='truncate max-w-md hover:underline'
                weight={fontWeights.medium}
              >
                {isPageHistoryTrackingEnabled
                  ? entryPageWithoutProtocol
                  : translate('panels.recordings.consentless.hiddenPages')}
              </Paragraph>
            </TooltipWrapper>
          </div>
        </a>
        <a
          target={'_blank'}
          rel='noreferrer'
          ref={exitPageRef}
          aria-disabled={!isPageHistoryTrackingEnabled}
          href={isPageHistoryTrackingEnabled ? exitPage : undefined}
          onClick={(event) => event.stopPropagation()}
        >
          <div className='flex items-center gap-2'>
            <TooltipWrapper content={translate('recordings.exitPage')}>
              <div>
                <LogoutIcon color='var(--color-text-secondary)' className='shrink-0' />
              </div>
            </TooltipWrapper>
            <TooltipWrapper
              content={
                isPageHistoryTrackingEnabled ? (
                  <p className='break-words'>{exitPage}</p>
                ) : (
                  translate('panels.recordings.consentless.hiddenPages')
                )
              }
              open={isExitPageFocused || undefined}
            >
              <Paragraph
                size={paragraphSizes.tiny}
                colorClassName='text-text-secondary'
                className='max-w-md truncate hover:underline'
                weight={fontWeights.medium}
              >
                {isPageHistoryTrackingEnabled
                  ? exitPageWithoutProtocol
                  : translate('panels.recordings.consentless.hiddenPages')}
              </Paragraph>
            </TooltipWrapper>
          </div>
        </a>
      </div>
    </div>
  );
};
